import React, { useState } from "react";
import { CalculationInputs } from "../../utils/calculationUtils";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { AlertCircle, Calculator, ArrowLeftRight, ChevronDown, ChevronUp } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../components/ui/collapsible";

interface InputFormProps {
  inputs: CalculationInputs;
  onInputChange: (key: keyof CalculationInputs, value: any) => void;
  onCalculate: () => void;
}

const InputForm: React.FC<InputFormProps> = ({
  inputs,
  onInputChange,
  onCalculate,
}) => {
  /** Keep as string so intermediate decimals like "12." / "12.50" are allowed while typing. */
  const [targetFinalRental, setTargetFinalRental] = useState("");
  const [showBackCalculation, setShowBackCalculation] = useState<boolean>(false);

  const handleNumberInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    key: keyof CalculationInputs
  ) => {
    const value = e.target.value;
    if (value === "") {
      onInputChange(key, 0);
    } else {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        onInputChange(key, numValue);
      }
    }
  };

  const handlePercentageInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    key: keyof CalculationInputs
  ) => {
    const value = e.target.value;
    if (value === "") {
      onInputChange(key, 0);
    } else {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        onInputChange(key, numValue / 100);
      }
    }
  };

  const calculateEnhancedValues = () => {
    const baseRental = inputs.totalRental / (1 + inputs.gstRate);
    const gstAmount = baseRental * inputs.gstRate;

    let gstPassback = 0;
    if (inputs.gstPassbackEnabled) {
      if (inputs.rentalFrequency === 'quarterly') {
        gstPassback = gstAmount / (inputs.totalMonths / 3);
      } else {
        gstPassback = gstAmount / inputs.totalMonths;
      }
      const round2 = (num: number) => Math.round((num + Number.EPSILON) * 100) / 100;
      gstPassback = round2(gstPassback);
      inputs.gstPassback = gstPassback;
    }

    let ptpmWithPassback = inputs.ptpmConsideration;
    if (inputs.gstPassbackEnabled) {
      ptpmWithPassback = inputs.ptpmConsideration - gstPassback;
    }

    let finalRental = 0;
    let finalRentalDescription = "";

    switch (inputs.rentalCalculationBasis) {
      case 'preGstDiscounting':
        finalRental = ptpmWithPassback;
        finalRentalDescription = "Pre GST Discounting (PTPM as Final)";
        break;
      case 'postGstDiscounting':
        finalRental = ptpmWithPassback * (1 + inputs.gstRate);
        finalRentalDescription = "Post GST Discounting (PTPM + GST)";
        break;
      case 'postTdsDiscounting':
        const ptpmAfterTds = ptpmWithPassback * 0.98;
        const gstOnPtpm = ptpmWithPassback * inputs.gstRate;
        finalRental = ptpmAfterTds + gstOnPtpm;
        finalRentalDescription = "Post TDS Discounting (PTPM*98% + GST on PTPM)";
        break;
      default:
        finalRental = ptpmWithPassback;
        finalRentalDescription = "Standard Calculation";
    }

    return {
      baseRental,
      gstAmount,
      gstPassback,
      ptpmWithPassback,
      finalRental,
      finalRentalDescription,
      tdsAmount: ptpmWithPassback * 0.02,
      securityDeposit: (inputs.securityDepositPercentage / 100) * inputs.totalRental,
      processingFee: (inputs.processingFeePercentage / 100) * inputs.totalRental,
      refundableSecurity: (inputs.refundableSecurityPercentage / 100) * inputs.totalRental
    };
  };

  const calculateRequiredPTPM = (targetFinalRental: number): number => {
    const baseRental = inputs.totalRental / (1 + inputs.gstRate);
    const gstAmount = baseRental * inputs.gstRate;

    let gstPassback = 0;
    if (inputs.gstPassbackEnabled) {
      if (inputs.rentalFrequency === 'quarterly') {
        gstPassback = gstAmount / (inputs.totalMonths / 3);
      } else {
        gstPassback = gstAmount / inputs.totalMonths;
      }
    }

    let requiredPTPMWithPassback = 0;

    switch (inputs.rentalCalculationBasis) {
      case 'preGstDiscounting':
        requiredPTPMWithPassback = targetFinalRental;
        break;
      case 'postGstDiscounting':
        requiredPTPMWithPassback = targetFinalRental / (1 + inputs.gstRate);
        break;
      case 'postTdsDiscounting':
        requiredPTPMWithPassback = targetFinalRental / (0.98 + inputs.gstRate);
        break;
      default:
        requiredPTPMWithPassback = targetFinalRental;
    }

    let requiredPTPMConsideration = requiredPTPMWithPassback;
    if (inputs.gstPassbackEnabled) {
      requiredPTPMConsideration = requiredPTPMWithPassback + gstPassback;
    }

    return requiredPTPMConsideration;
  };

  const handleBackCalculation = () => {
    const amount = parseFloat(targetFinalRental.replace(/,/g, ""));
    if (!Number.isNaN(amount) && amount > 0) {
      const requiredPTPM = calculateRequiredPTPM(amount);
      onInputChange("ptpmConsideration", requiredPTPM);
      setTargetFinalRental("");
      setShowBackCalculation(false);
    }
  };

  const calculatedValues = calculateEnhancedValues();

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const getFrequencyLabel = () => {
    return inputs.rentalFrequency === 'quarterly' ? 'quarter' : 'month';
  };

  const getPtpmLabel = () => {
    return inputs.rentalFrequency === 'quarterly' ? 'PTPQ Consideration' : 'PTPM Consideration';
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Basic Parameters */}
      <Card className="border-[#e3d6b7]" style={{ background: "linear-gradient(to right, #f8f6f1, #f0eade)" }}>
        <CardHeader className="pb-2 sm:pb-3">
          <CardTitle className="text-base sm:text-lg text-black flex items-center gap-2">
            <Calculator className="h-4 w-4 sm:h-5 sm:w-5" />
            Basic Parameters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 sm:space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-2">
              <Label htmlFor="totalMonths" className="text-sm">Total Months</Label>
              <Input
                id="totalMonths"
                type="number"
                value={inputs.totalMonths || ""}
                onChange={(e) => handleNumberInput(e, "totalMonths")}
                min="1"
                className="w-full text-sm"
                onWheel={(e) => e.currentTarget.blur()}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="rentalFrequency" className="text-sm">Rental Frequency</Label>
              <Select
                value={inputs.rentalFrequency}
                onValueChange={(value) =>
                  onInputChange("rentalFrequency", value)
                }
              >
                <SelectTrigger className="w-full bg-white">
                  <SelectValue placeholder="Select Frequency" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm">Total Asset Value</Label>
            <Input
              type="text"
              value={inputs.totalRental || ""}
              onChange={(e) => handleNumberInput(e, "totalRental")}
              className="w-full font-medium text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="disbursementAmount" className="text-sm">Disbursement Amount</Label>
            <Input
              id="disbursementAmount"
              type="number"
              value={Number(inputs.disbursementAmount || inputs.totalRental || 0).toFixed(2)}
              readOnly
              className="w-full bg-gradient-to-r from-gray-100 to-gray-200 cursor-not-allowed font-bold text-gray-800 text-sm"
              onWheel={(e) => e.currentTarget.blur()}
            />
            <p className="text-xs text-blue-600 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              Calculated automatically using Goal Seek
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ptpmConsideration" className="text-sm">{getPtpmLabel()}</Label>
            <Input
              id="ptpmConsideration"
              type="number"
              value={inputs.ptpmConsideration || ""}
              onChange={(e) => handleNumberInput(e, "ptpmConsideration")}
              className="w-full text-sm"
              step="0.01"
              onWheel={(e) => e.currentTarget.blur()}
            />
          </div>
        </CardContent>
      </Card>

      {/* GST and Calculation Parameters */}
      <Card className="border-[#7bb3bd]" style={{ background: "linear-gradient(to right, #dfeff1, #a4cdd4)" }}>
        <CardHeader className="pb-2 sm:pb-3">
          <CardTitle className="text-base sm:text-lg text-black">GST & Calculation Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 sm:space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-2">
              <Label htmlFor="gstRate" className="text-sm">GST Rate</Label>
              <Select
                value={
                  inputs.gstRate === 0 || inputs.gstRate == null
                    ? "0"
                    : Number(inputs.gstRate).toFixed(2)
                }
                onValueChange={(value) =>
                  onInputChange("gstRate", parseFloat(value))
                }
              >
                <SelectTrigger className="w-full bg-white">
                  <SelectValue placeholder="Select GST Rate" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="0.40">40%</SelectItem>
                  <SelectItem value="0.28">28%</SelectItem>
                  <SelectItem value="0.18">18%</SelectItem>
                  <SelectItem value="0.12">12%</SelectItem>
                  <SelectItem value="0.05">5%</SelectItem>
                  <SelectItem value="0">0%</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-600">
                Amount: {formatCurrency(calculatedValues.gstAmount)}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="discountingFactor" className="text-sm">Discounting Factor (%)</Label>
              <Input
                id="discountingFactor"
                type="number"
                value={inputs.discountingFactor ? parseFloat((inputs.discountingFactor * 100).toFixed(10)) : ""}
                onChange={(e) => handlePercentageInput(e, "discountingFactor")}
                className="w-full text-sm"
                onWheel={(e) => e.currentTarget.blur()}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-white rounded-lg border space-y-2 sm:space-y-0">
            <div className="space-y-1 flex-1">
              <Label htmlFor="gstPassback" className="text-sm sm:text-base font-medium">
                GST Passback
              </Label>
              <p className="text-xs sm:text-sm text-gray-600">
                {inputs.gstPassbackEnabled
                  ? `GST amount will be deducted from ${inputs.rentalFrequency === 'quarterly' ? 'PTPQ' : 'PTPM'}: ${formatCurrency(calculatedValues.gstPassback)}/${getFrequencyLabel()}`
                  : `No GST passback - ${inputs.rentalFrequency === 'quarterly' ? 'PTPQ' : 'PTPM'} remains unchanged`
                }
              </p>
            </div>
            <Switch className="data-[state=checked]:bg-[#f0eade] data-[state=unchecked]:bg-gray-300"
              id="gstPassback"
              checked={inputs.gstPassbackEnabled}
              onCheckedChange={(checked) =>
                onInputChange("gstPassbackEnabled", checked)
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="rentalCalculationBasis" className="text-sm">Rental Calculation Basis</Label>
            <Select
              value={inputs.rentalCalculationBasis}
              onValueChange={(value) =>
                onInputChange("rentalCalculationBasis", value)
              }
            >
              <SelectTrigger className="w-full bg-white">
                <SelectValue placeholder="Select Calculation Basis" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="preGstDiscounting">Pre GST Discounting</SelectItem>
                <SelectItem value="postGstDiscounting">Post GST Discounting</SelectItem>
                <SelectItem value="postTdsDiscounting">Post TDS Discounting</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-600">
              Rental Amount: {formatCurrency(calculatedValues.ptpmWithPassback)}
            </p>
            <p className="text-xs text-gray-600">
              Rental Amount ({inputs.rentalCalculationBasis === 'preGstDiscounting' ? 'Pre GST' : inputs.rentalCalculationBasis === 'postGstDiscounting' ? 'Post GST' : 'Post TDS'}): {formatCurrency(calculatedValues.finalRental)}
            </p>

            <div className="w-full space-y-2">
              <Collapsible open={showBackCalculation} onOpenChange={setShowBackCalculation} className="w-full">
                <div className="flex w-full items-center justify-between">
                  <CollapsibleTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="self-start p-1 h-auto font-medium text-black hover:text-black sm:self-auto"
                    >
                      <span className="text-xs sm:text-sm">Back Calculation</span>
                      {showBackCalculation ? (
                        <ChevronUp className="ml-1 h-3 w-3" />
                      ) : (
                        <ChevronDown className="ml-1 h-3 w-3" />
                      )}
                    </Button>
                  </CollapsibleTrigger>
                </div>
                <CollapsibleContent className="w-full">
                  <div className="mt-2 w-full space-y-2 rounded-lg border border-blue-200 bg-[#edf6f7] p-3 animate-in slide-in-from-top-2 duration-200">
                    <div className="flex items-center gap-2">
                      <ArrowLeftRight className="h-4 w-4 text-black" />
                      <Label htmlFor="targetFinalRental" className="text-sm font-medium text-black">
                        Target Final Rental
                      </Label>
                    </div>
                    <div className="flex w-full flex-col gap-2">
                      <Input
                        id="targetFinalRental"
                        type="text"
                        inputMode="decimal"
                        value={targetFinalRental}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/,/g, "");
                          // Allow empty, digits, and a single decimal point (incl. trailing ".")
                          if (raw === "" || /^\d*\.?\d*$/.test(raw)) {
                            setTargetFinalRental(raw);
                          }
                        }}
                        placeholder="Enter target final rental amount"
                        className="w-full min-w-full text-sm"
                        onWheel={(e) => e.currentTarget.blur()}
                      />
                      <Button
                        type="button"
                        onClick={handleBackCalculation}
                        disabled={
                          !targetFinalRental ||
                          !(parseFloat(targetFinalRental) > 0)
                        }
                        size="sm"
                        variant="outline"
                        className="w-full px-4 sm:w-auto sm:self-start"
                      >
                        Calculate
                      </Button>
                    </div>
                    <p className="text-xs text-black">
                      Enter a target final rental amount to calculate the required {getPtpmLabel()}
                    </p>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security Deposits and Fees */}
      <Card className="border-[#e3d6b7]" style={{ background: "linear-gradient(to right, #f8f6f1, #f0eade)" }}>
        <CardHeader className="pb-2 sm:pb-3">
          <CardTitle className="text-base sm:text-lg text-black">Security Deposits & Fees</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 sm:space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-2">
              <Label htmlFor="securityDepositPercentage" className="text-sm">Security Deposit (%)</Label>
              <Input
                id="securityDepositPercentage"
                type="number"
                value={inputs.securityDepositPercentage || ""}
                onChange={(e) => handleNumberInput(e, "securityDepositPercentage")}
                className="w-full text-sm"
                step="0.1"
                onWheel={(e) => e.currentTarget.blur()}
              />
              <p className="text-xs text-gray-600">
                Amount: {formatCurrency(calculatedValues.securityDeposit)}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="processingFeePercentage" className="text-sm">Processing Fee (%)</Label>
              <Input
                id="processingFeePercentage"
                type="number"
                value={inputs.processingFeePercentage || ""}
                onChange={(e) => handleNumberInput(e, "processingFeePercentage")}
                className="w-full text-sm"
                step="0.1"
                onWheel={(e) => e.currentTarget.blur()}
              />
              <p className="text-xs text-gray-600">
                Amount: {formatCurrency(calculatedValues.processingFee)}
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="refundableSecurityPercentage" className="text-sm">Refundable Security Deposit (%)</Label>
            <Input
              id="refundableSecurityPercentage"
              type="number"
              value={inputs.refundableSecurityPercentage || ""}
              onChange={(e) => handleNumberInput(e, "refundableSecurityPercentage")}
              className="w-full text-sm"
              step="0.1"
              onWheel={(e) => e.currentTarget.blur()}
            />
            <p className="text-xs text-gray-600">
              Amount: {formatCurrency(calculatedValues.refundableSecurity)}
            </p>
           </div>

          {/* Advance Rental Toggle */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-white rounded-lg border space-y-2 sm:space-y-0">
            <div className="space-y-1 flex-1">
              <Label htmlFor="advanceRental" className="text-sm sm:text-base font-medium">
                Advance Rental
              </Label>
              <p className="text-xs sm:text-sm text-gray-600">
                {inputs.advanceRentalEnabled
                  ? inputs.advanceRentalType === 'percentage'
                    ? `Advance rental of ${inputs.advanceRentalCount}% of invoice value`
                    : `Advance rental of ${inputs.advanceRentalCount} ${inputs.rentalFrequency === 'quarterly' ? 'quarter(s)' : 'month(s)'}`
                  : 'No advance rental'}
              </p>
            </div>
            <Switch
              className="data-[state=checked]:bg-[#f0eade] data-[state=unchecked]:bg-gray-300"
              id="advanceRental"
              checked={inputs.advanceRentalEnabled}
              onCheckedChange={(checked) => onInputChange("advanceRentalEnabled", checked)}
            />
          </div>

          {inputs.advanceRentalEnabled && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-sm">Advance Rental Type</Label>
                  <Select
                    value={inputs.advanceRentalType || 'periods'}
                    onValueChange={(value) => {
                      onInputChange("advanceRentalType", value);
                      onInputChange("advanceRentalCount", 0);
                    }}
                  >
                    <SelectTrigger className="w-full text-sm bg-white">
                      <SelectValue className="bg-white" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem
                        value="periods"
                        className="data-[state=checked]:bg-white data-[state=checked]:text-foreground"
                      >
                        No. of {inputs.rentalFrequency === 'quarterly' ? 'Quarters' : 'Months'}
                      </SelectItem>
                      <SelectItem
                        value="percentage"
                        className="data-[state=checked]:bg-white data-[state=checked]:text-foreground"
                      >
                        % of Invoice Value
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="advanceRentalCount" className="text-sm">
                    {inputs.advanceRentalType === 'percentage'
                      ? 'Advance Rental (%)'
                      : `Advance Rentals (${inputs.rentalFrequency === 'quarterly' ? 'quarters' : 'months'})`}
                  </Label>
                  <Input
                    id="advanceRentalCount"
                    type="number"
                    value={inputs.advanceRentalCount || ""}
                    onChange={(e) => handleNumberInput(e, "advanceRentalCount")}
                    className="w-full text-sm"
                    min="0"
                    step={inputs.advanceRentalType === 'percentage' ? '0.1' : '1'}
                    onWheel={(e) => e.currentTarget.blur()}
                  />
                </div>
              </div>
              <p className="text-xs text-gray-600">
                Advance Rental Amount: {formatCurrency(
                  inputs.advanceRentalType === 'percentage'
                    ? inputs.totalRental * ((inputs.advanceRentalCount || 0) / 100)
                    : calculatedValues.ptpmWithPassback * (inputs.advanceRentalCount || 0)
                )}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Date Parameters */}
      <Card className="border-[#7bb3bd]" style={{ background: "linear-gradient(to right, #dfeff1, #a4cdd4)" }}>
        <CardHeader className="pb-2 sm:pb-3">
          <CardTitle className="text-base sm:text-lg text-black">Date Parameters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 sm:space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-2">
              <Label htmlFor="disbursementDate" className="text-sm">Disbursement Date</Label>
              <Input
                id="disbursementDate"
                type="date"
                value={
                  inputs.disbursementDate
                    ? new Date(inputs.disbursementDate).toISOString().split("T")[0]
                    : ""
                }
                onChange={(e) => onInputChange("disbursementDate", e.target.value)}
                className="w-full text-sm"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dueDate" className="text-sm">First Due Date</Label>
              <Input
                id="dueDate"
                type="date"
                value={
                  inputs.dueDate
                    ? new Date(inputs.dueDate).toISOString().split("T")[0]
                    : ""
                }
                onChange={(e) => onInputChange("dueDate", e.target.value)}
                className="w-full text-sm"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Calculate Button */}
      <Button
        onClick={onCalculate}
        className="w-full bg-[#cec8b0] hover:bg-[#e5d9bd] text-white font-semibold py-3 text-sm sm:text-lg"
      >
        Calculate
      </Button>

      {/* Real-time Calculated Values Display */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardHeader>
          <CardTitle className="text-base sm:text-lg text-blue-900">Live Calculation Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-2 sm:space-y-3">
              <div className="flex justify-between items-center p-2 bg-white rounded text-sm">
                <span className="font-medium text-blue-700">Base Rental:</span>
                <span className="font-bold text-blue-900">{formatCurrency(calculatedValues.baseRental)}</span>
              </div>
              <div className="flex justify-between items-center p-2 bg-white rounded text-sm">
                <span className="font-medium text-blue-700">GST Amount:</span>
                <span className="font-bold text-blue-900">{formatCurrency(calculatedValues.gstAmount)}</span>
              </div>
              <div className="flex justify-between items-center p-2 bg-white rounded text-sm">
                <span className="font-medium text-blue-700">GST Passback (Per {getFrequencyLabel()}):</span>
                <span className="font-bold text-blue-900">{formatCurrency(calculatedValues.gstPassback)}</span>
              </div>
            </div>
            <div className="space-y-2 sm:space-y-3">
              <div className="flex justify-between items-center p-2 bg-white rounded text-sm">
                <span className="font-medium text-blue-700">{inputs.rentalFrequency === 'quarterly' ? 'PTPQ' : 'PTPM'} (Post Passback):</span>
                <span className="font-bold text-blue-900">{formatCurrency(calculatedValues.ptpmWithPassback)}</span>
              </div>
              <div className="flex justify-between items-center p-2 bg-white rounded text-sm">
                <span className="font-medium text-blue-700">Final Rental:</span>
                <span className="font-bold text-green-700 text-sm sm:text-lg">{formatCurrency(calculatedValues.finalRental)}</span>
              </div>
              <div className="flex justify-between items-center p-2 bg-white rounded text-sm">
                <span className="font-medium text-blue-700">TDS Amount (2%):</span>
                <span className="font-bold text-blue-900">{formatCurrency(calculatedValues.tdsAmount)}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InputForm;